import React, { useState, useRef, useEffect, useCallback } from "react";
import { api } from "../utils/api";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function PhotoUpload({ entityType, entityId, maxPhotos = 1, onUpdate }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const data = await api(`/api/photos/${entityType}/${entityId}`);
      setPhotos(data.photos || []);
    } catch (err) {
      console.error("Fetch photos error:", err);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (entityId) fetchPhotos();
  }, [entityId, fetchPhotos]);

  async function handleUpload(file) {
    if (!file) return;
    if (!entityId) {
      setError("Impossible d'uploader : profil non encore chargé. Sauvegardez d'abord votre profil.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Format non supporté. Utilisez JPG, PNG ou WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Fichier trop volumineux (max 5 Mo).");
      return;
    }

    setUploading(true);
    setError("");

    const formData = new FormData();
    formData.append("photo", file);
    formData.append("entity_type", entityType);
    formData.append("entity_id", entityId);

    try {
      const token = localStorage.getItem("sensoria_token");
      const res = await fetch(`${API_BASE}/api/photos/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Échec de l'upload.");
      }

      setPhotos((prev) => [...prev, data.photo]);
      onUpdate?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDelete(photoId) {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api(`/api/photos/${photoId}`, { method: "DELETE" });
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      onUpdate?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSetPrimary(photoId) {
    try {
      await api(`/api/photos/${photoId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_primary: true }),
      });
      setPhotos((prev) =>
        prev.map((p) => ({ ...p, is_primary: p.id === photoId }))
      );
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleToggleBlur(photoId, currentBlur) {
    try {
      await api(`/api/photos/${photoId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_blurred: !currentBlur }),
      });
      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, is_blurred: !currentBlur } : p))
      );
    } catch (err) {
      setError(err.message);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }

  return (
    <div className="space-y-3">
      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((photo) => (
            <div key={photo.id} className="group relative aspect-square overflow-hidden rounded-xl bg-sensoria-surface">
              <img
                src={photo.url}
                alt={photo.caption || "Photo"}
                className={`h-full w-full object-cover transition-all ${
                  photo.is_blurred ? "blur-lg" : ""
                }`}
              />
              {photo.is_primary && (
                <span className="absolute left-1.5 top-1.5 rounded-full bg-sensoria-accent px-1.5 py-0.5 text-[9px] font-bold text-white">
                  ★
                </span>
              )}
              {photo.is_blurred && (
                <span className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] text-white">
                  🔒
                </span>
              )}

              {/* Actions overlay */}
              <div className="absolute inset-0 flex items-end justify-center gap-1 bg-gradient-to-t from-black/70 via-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                {!photo.is_primary && (
                  <button
                    onClick={() => handleSetPrimary(photo.id)}
                    className="rounded-lg bg-white/20 px-2 py-1 text-[10px] text-white backdrop-blur-sm hover:bg-white/30"
                    title="Photo principale"
                  >
                    ★
                  </button>
                )}
                <button
                  onClick={() => handleToggleBlur(photo.id, photo.is_blurred)}
                  className="rounded-lg bg-white/20 px-2 py-1 text-[10px] text-white backdrop-blur-sm hover:bg-white/30"
                  title={photo.is_blurred ? "Rendre visible" : "Flouter"}
                >
                  {photo.is_blurred ? "👁" : "🔒"}
                </button>
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="rounded-lg bg-red-500/40 px-2 py-1 text-[10px] text-white backdrop-blur-sm hover:bg-red-500/60"
                  title="Supprimer"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {photos.length < maxPhotos && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-all ${
            dragOver
              ? "border-sensoria-accent bg-sensoria-accent/5"
              : "border-sensoria-border hover:border-sensoria-muted"
          } ${uploading ? "pointer-events-none opacity-50" : ""}`}
        >
          {uploading ? (
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
              <span className="text-sm text-sensoria-text-dim">Upload en cours...</span>
            </div>
          ) : (
            <>
              <span className="mb-1 text-2xl">📷</span>
              <span className="text-xs text-sensoria-text-dim">
                Cliquez ou glissez une image (JPG, PNG, WebP · max 5 Mo)
              </span>
              <span className="mt-1 text-[10px] text-sensoria-muted">
                {photos.length}/{maxPhotos} photos
              </span>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleUpload(e.target.files[0])}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-3 py-2 text-xs text-sensoria-danger">
          {error}
          <button onClick={() => setError("")} className="ml-2 underline">fermer</button>
        </div>
      )}
    </div>
  );
}
